import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ComponentRef, Injectable, Injector, Type } from '@angular/core';
import { BottomSheetConfig } from './bottom-sheet-config';
import { BottomSheet, BottomSheetRef } from './bottom-sheet-ref';
import { BOTTOM_SHEET_DATA } from './bottom-sheet-tokens';

@Injectable({ providedIn: 'root' })
export class BottomSheetService {

  private bottomSheetRef?: BottomSheetRef;

  constructor(private overlay: Overlay, 
              private injector: Injector) {
  }

  openBottomSheet<T extends BottomSheet, D = any, R = any>(
    component: Type<T>,
    config: BottomSheetConfig<D> = {}
  ): BottomSheetRef<R> {
    
    this.closeBottomSheet(); // close any existing sheet
    
    const overlayRef: OverlayRef = this.overlay.create({
      hasBackdrop: !config.disableBackdrop,
      backdropClass: 'cdk-overlay-dark-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay.position()
        .global()
        .bottom('0')  
        .centerHorizontally(),
      panelClass: ['bottom-sheet', ...([] as string[]).concat(config.panelClass ?? [])]
    });

    const bottomSheetRef = new BottomSheetRef<R>(overlayRef);
    const injector = this.createInjector(bottomSheetRef, config.data, config.animationDuration || 300);
    const portal: ComponentPortal<T> = new ComponentPortal(component, null, injector);
    const componentRef: ComponentRef<T> = overlayRef.attach(portal);
    const bottomSheetComponent = componentRef.instance;

    if (!config.disableBackdrop) {
      overlayRef.backdropClick().subscribe(() => {
        if (!config.disableClose) bottomSheetRef.dispose();
      });
    }

    // ESC key
    const escapeHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !config.disableClose) bottomSheetRef.dispose();
    };
    document.addEventListener('keydown', escapeHandler);

    bottomSheetComponent.onClosed.subscribe(() => {
      document.removeEventListener('keydown', escapeHandler);
      bottomSheetRef.dispose();
    });
    
    this.bottomSheetRef = bottomSheetRef;
    return bottomSheetRef;
  }

  private createInjector<D, R>(bottomSheetRef: BottomSheetRef<R>, 
                                data: D | undefined, 
                                animationDuration: number): Injector {
    // const tokens = new WeakMap();
    // tokens.set(BottomSheetRef, bottomSheetRef);
    // tokens.set(BOTTOM_SHEET_DATA, { ...data, animationDuration });
    // return new PortalInjector(this.injector, tokens);
    return Injector.create({
      providers: [
        { provide: BottomSheetRef, useValue: bottomSheetRef },
        { provide: BOTTOM_SHEET_DATA, useValue: { ...data, animationDuration } }
      ]
    });
  }

  closeBottomSheet() {
    if (this.bottomSheetRef) {
      this.bottomSheetRef.dispose();
      this.bottomSheetRef = undefined;
    }
  }
}
